// Shared state + capture engine for composed experiences. Both the standard
// themed renderer (ComposedExperienceSlide) and the Tier-A world skins
// (ComposedExperienceObsidian, …) consume this hook so the capture contract —
// one composite record, fired once, when every actionable block is committed —
// is identical across skins and can't drift. Presentation is per-skin; the
// state machine is here, once.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type { CompositionBlock } from '../../../../../lib/scenario-builder/composition/types';
import {
  buildCapture,
  closeValues,
  rollupScore,
  type Answers,
  type BlockAnswer,
} from '../../../../../lib/scenario-builder/composition/capture';

const ACTIONABLE = new Set([
  'choice',
  'rate-slider',
  'sort-zones',
  'select-evidence',
  'confidence',
  'flag-in-text',
]);

export interface ComposedExperienceController {
  blocks: CompositionBlock[];
  answers: Answers;
  setAnswer: (id: string, answer: BlockAnswer) => void;
  /** True once every actionable block has been committed. */
  complete: boolean;
  /** Block-scoped {id.token} values for the close. */
  values: ReturnType<typeof closeValues>;
  /** 0–1 rollup score for the experience (mode 'none' → 0, unused). */
  rollup: number;
}

interface Args {
  data: ComposedExperienceSlideData;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function useComposedExperience({
  data,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Args): ComposedExperienceController {
  const blocks = useMemo<CompositionBlock[]>(() => data.blocks ?? [], [data.blocks]);

  // Rehydrate prior answers from a saved composite capture record.
  const initialAnswers = useMemo<Answers>(() => {
    const saved = savedResponse as { blocks?: Record<string, { answer?: BlockAnswer }> } | null | undefined;
    const out: Answers = {};
    if (saved?.blocks) {
      for (const [id, rec] of Object.entries(saved.blocks)) {
        if (rec?.answer) out[id] = rec.answer;
      }
    }
    return out;
  }, [savedResponse]);

  const [answers, setAnswers] = useState<Answers>(initialAnswers);
  const firedRef = useRef(false);

  const setAnswer = (id: string, answer: BlockAnswer) =>
    setAnswers((prev) => ({ ...prev, [id]: answer }));

  const actionableIds = useMemo(
    () => blocks.filter((b) => ACTIONABLE.has(b.type)).map((b) => b.id),
    [blocks],
  );
  const complete = actionableIds.length > 0 && actionableIds.every((id) => answers[id] !== undefined);

  // Fire the ONE composite capture record + onComplete, once, when the learner
  // has committed every actionable block.
  useEffect(() => {
    if (!complete || firedRef.current || isEditing) return;
    firedRef.current = true;
    const capture = buildCapture(blocks, data.scoring, answers);
    onActivityResponse?.({
      activityType: 'composed-experience',
      mode: data.scoring.mode === 'none' ? 'survey' : 'quiz',
      responseData: capture.responseData,
      ...(capture.score !== null ? { score: capture.score } : {}),
    });
    onComplete?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  const values = useMemo(() => closeValues(blocks, answers), [blocks, answers]);
  const rollup = useMemo(() => rollupScore(blocks, data.scoring, answers), [blocks, data.scoring, answers]);

  return { blocks, answers, setAnswer, complete, values, rollup };
}
